import type {
  TradeRowResponse,
  TradeSections,
} from '@/entities/business-relationship/api/businessRelationship.api';
import type {
  RelationshipGroup,
  StatusBadgeStyle,
} from '@/entities/business-relationship/lib/statusView';

/**
 * M-19 · M-20 거래 정보 드로어의 행 (Figma 거래처_거래정보 840:4980) — 의원급(제약사)과 병원급(의약품)을 같은 모양으로 맞춘다.
 * 섹션(거래중 · 필터링 신청 · 종료·거절)은 서버가 정한 값을 그대로 쓴다.
 */
export interface TradeRow {
  key: string;
  group: RelationshipGroup;
  /** 의원급: 제약사명 / 병원급: 의약품명 */
  title: string;
  /** 병원급만 — 날짜 줄 앞의 제약사명 */
  pharmaCompanyName: string | null;
  /** 제목 위 배지. 거래중 행은 없다 (Type=의원_기본 · 병원_기본) */
  badge: { label: string; style: StatusBadgeStyle } | null;
  /** 행에 표시하는 날짜와 그 뒤에 붙는 말(승인 · 신청 · 종료 · 거절). 값이 없으면 null */
  date: string | null;
  dateSuffix: string;
  minPrescriptionAmount: number | null;
  /** 이관 확인 중 — 금액 대신 「타 CSO 이관 확인 중」 */
  transferPending: boolean;
  /** 병원급 의약품 행만. RU 코드("-")는 null로 바꿔 M-12로 열지 않는다 */
  drugCode: string | null;
}

/** 신청 쪽 배지 이름 — 서버 statusView "신청중"은 Figma대로 "거래 신청"으로 보인다 (905:14010~14014) */
const applyingLabel = (row: TradeRowResponse) => {
  switch (row.transactionStatus) {
    case 'APPLIED':
    case 'RESUBMITTED':
      return '거래 신청';
    case 'IN_REVIEW':
      return '확인 중';
    case 'PENDING_TRANSFER':
      return '이관 확인 중';
    default:
      return row.statusView;
  }
};

const badgeOf = (
  group: RelationshipGroup,
  row: TradeRowResponse,
): TradeRow['badge'] => {
  if (group === 'ongoing') return null;
  // 신청 쪽은 모두 Default strong(파랑)
  if (group === 'applying')
    return {
      label: applyingLabel(row),
      style: { state: 'default', emphasis: 'strong' },
    };
  if (row.transactionStatus === 'REJECTED')
    return {
      label: '신청 거절',
      style: { state: 'error', emphasis: 'strong' },
    };
  // 폐업 · 제약사 거래 종료로 막힌 의약품은 서버가 "거래 불가"로 준다
  return {
    label: row.statusView === '거래 불가' ? '거래 불가' : '거래 종료',
    style: { state: 'disabled', emphasis: 'strong' },
  };
};

/** 섹션별 날짜 — 병원급은 의약품 날짜를 먼저, 없으면 제약사 날짜 */
const dateOf = (
  group: RelationshipGroup,
  row: TradeRowResponse,
): { date: string | null; dateSuffix: string } => {
  if (group === 'ongoing')
    return {
      date: row.drugApprovalDate ?? row.approvalDate,
      dateSuffix: '승인',
    };
  if (group === 'applying')
    return {
      date: row.drugApplicationDate ?? row.applicationDate,
      dateSuffix: '신청',
    };
  return {
    date: row.terminatedAt,
    dateSuffix: row.transactionStatus === 'REJECTED' ? '거절' : '종료',
  };
};

const toTradeRow = (
  row: TradeRowResponse,
  group: RelationshipGroup,
  isHospital: boolean,
): TradeRow => {
  const drugCode = row.drugCode && row.drugCode !== '-' ? row.drugCode : null;
  return {
    key: row.tradeId,
    group,
    title: isHospital
      ? (row.drugName ?? row.drugCode ?? '—')
      : row.pharmaCompanyName,
    pharmaCompanyName: isHospital ? row.pharmaCompanyName : null,
    badge: badgeOf(group, row),
    ...dateOf(group, row),
    minPrescriptionAmount: row.minPrescriptionAmount,
    transferPending: row.transactionStatus === 'PENDING_TRANSFER',
    drugCode,
  };
};

/** 서버 섹션 → 드로어 섹션별 행. 정렬은 서버 순서 그대로 */
export const toTradeSections = (
  sections: TradeSections,
  isHospital: boolean,
): Record<RelationshipGroup, TradeRow[]> => ({
  ongoing: sections.ongoing.map((r) => toTradeRow(r, 'ongoing', isHospital)),
  applying: sections.applying.map((r) => toTradeRow(r, 'applying', isHospital)),
  closed: sections.closed.map((r) => toTradeRow(r, 'closed', isHospital)),
});
